import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import {
  Box,
  Text,
  Spinner,
  Center,
  HStack,
  Pressable,
  Icon,
} from '@gluestack-ui/themed';
import { ZoomIn, ZoomOut, Move } from 'lucide-react-native';
import log from '../../../../common/utils/logger';

declare global {
  interface Window {
    mermaid: {
      initialize: (config: object) => void;
      render: (id: string, code: string) => Promise<{ svg: string; bindFunctions?: (el: Element) => void }>;
    };
  }
}

interface MermaidDiagramProps {
  code: string;
  height?: number;
  horizontal?: boolean;
  zoomLevel?: number;
  onError?: (error: string) => void;
}

/**
 * MermaidDiagram component for rendering mermaid diagrams
 * Based on recommended React patterns from the Mermaid community
 */
const MermaidDiagram = ({
  code,
  height = 400,
  horizontal = true,
  zoomLevel = 1.0,
  onError,
}: MermaidDiagramProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentZoom, setCurrentZoom] = useState(zoomLevel);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const containerRef = useRef<any>(null);
  const diagramRef = useRef<HTMLDivElement>(null);
  const uniqueId = useRef(`mermaid-${Math.random().toString(36).substring(2, 11)}`);

  // Process code to ensure horizontal layout if requested
  const processCode = useCallback((sourceCode: string) => {
    if (!sourceCode || sourceCode.trim() === '') {
      return '';
    }

    if (!horizontal) return sourceCode;

    let result = sourceCode;
    // Replace 'graph TD' or similar with 'graph LR' (left to right)
    if (sourceCode.match(/graph\s+(TD|TB)/i)) {
      result = sourceCode.replace(/graph\s+(TD|TB)/i, 'graph LR');
    } else if (sourceCode.match(/graph\s*$/m) || sourceCode.match(/graph\s+[^LRTDBU]/i)) {
      // If no direction specified, assume it's TD and replace with LR
      result = sourceCode.replace(/graph(\s*|(?:\s+[^LRTDBU]))/i, 'graph LR$1');
    }
    return result;
  }, [horizontal]);

  // Define mermaid configuration
  const getMermaidConfig = () => ({
    theme: 'default',
    securityLevel: 'strict',
    flowchart: {
      htmlLabels: true,
      curve: 'basis',
      useMaxWidth: false,
      rankSpacing: 60,
      nodeSpacing: 25,
      wrappingWidth: 50,
      padding: 15,
    },
    startOnLoad: false,
    logLevel: 'error',
  });

  const handleError = useCallback((err: Error | { message?: string }) => {
    log.error('Error rendering mermaid diagram:', err);
    const errorMessage = `Error rendering diagram: ${err?.message || 'Unknown error'}`;
    setError(errorMessage);
    setIsLoading(false);
    if (onError) {
      onError(errorMessage);
    }
  }, [onError]);

  const renderDiagram = useCallback(async () => {
    if (!code || code.trim() === '' || !diagramRef.current || !window.mermaid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      window.mermaid.initialize(getMermaidConfig());
      const processedCode = processCode(code);

      const { svg, bindFunctions } = await window.mermaid.render(
        `diagram-${uniqueId.current}`,
        processedCode,
      );

      if (diagramRef.current) {
        diagramRef.current.innerHTML = svg;
        if (bindFunctions) {
          bindFunctions(diagramRef.current);
        }
      }

      setIsLoading(false);
    } catch (err) {
      handleError(err as Error);
    }
  }, [code, handleError, processCode]);

  // Load mermaid script once when component mounts
  useEffect(() => {
    const loadMermaid = async () => {
      if (!document.getElementById('mermaid-script')) {
        return new Promise((resolve) => {
          const script = document.createElement('script');
          script.id = 'mermaid-script';
          script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';
          script.async = true;
          script.onload = () => {
            resolve(window.mermaid);
          };
          document.body.appendChild(script);
        });
      }
      if (window.mermaid) {
        return Promise.resolve(window.mermaid);
      }
      return Promise.resolve();
    };

    loadMermaid().then(() => {
      renderDiagram();
    });
  }, [renderDiagram]);

  // Render diagram whenever code changes
  useEffect(() => {
    if (window.mermaid) {
      renderDiagram();
    }
  }, [code, horizontal, renderDiagram]);

  const scaleValue = currentZoom || 1;
  const translateX = pan.x / (currentZoom || 1) || 0;
  const translateY = pan.y / (currentZoom || 1) || 0;

  if (!code || code.trim() === '') {
    return (
      <Center p="$4">
        <Text color="$textSubtle">No diagram data available</Text>
      </Center>
    );
  }

  return (
    <Box height={height} width="100%" position="relative">
      {isLoading && (
        <Center position="absolute" width="100%" height="100%" zIndex={10}>
          <Spinner size="large" />
        </Center>
      )}

      {error && (
        <Box p="$4" bg="$statusErrorSubtle" borderRadius="$md" borderWidth={1} borderColor="$statusError">
          <Text color="$statusError" size="xs">{error}</Text>
        </Box>
      )}

      {/* Zoom and Pan Controls */}
      <HStack
        position="absolute"
        top="$2"
        right="$2"
        zIndex={20}
        space="xs"
        bg="$bgOverlay"
        borderRadius="$md"
        p="$1"
      >
        <Pressable
          onPress={() => setCurrentZoom((prev) => Math.min(prev + 0.1, 2.0))}
          p="$1.5"
          borderRadius="$full"
          $hover={{ bg: '$bgHover' }}
        >
          <Icon as={ZoomIn} size="xs" color="$brandBase" />
        </Pressable>
        <Pressable
          onPress={() => setCurrentZoom((prev) => Math.max(prev - 0.1, 0.5))}
          p="$1.5"
          borderRadius="$full"
          $hover={{ bg: '$bgHover' }}
        >
          <Icon as={ZoomOut} size="xs" color="$brandBase" />
        </Pressable>
        <Pressable
          onPress={() => setPan({ x: 0, y: 0 })}
          p="$1.5"
          borderRadius="$full"
          $hover={{ bg: '$bgHover' }}
        >
          <Icon as={Move} size="xs" color="$brandBase" />
        </Pressable>
      </HStack>

      <Box
        ref={containerRef}
        width="100%"
        height="100%"
        opacity={isLoading ? 0.3 : 1}
        overflow="hidden"
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            width: '100%',
            height: '100%',
            cursor: isPanning ? 'grabbing' : 'grab',
          }}
          onMouseDown={(e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('svg')) {
              setIsPanning(true);
              setStartPan({
                x: e.clientX - pan.x,
                y: e.clientY - pan.y,
              });
            }
          }}
          onMouseMove={(e: React.MouseEvent) => {
            if (!isPanning) return;
            setPan({
              x: e.clientX - startPan.x,
              y: e.clientY - startPan.y,
            });
          }}
          onMouseUp={() => setIsPanning(false)}
          onMouseLeave={() => setIsPanning(false)}
        >
          <Box
            width="100%"
            display="flex"
            justifyContent="center"
            sx={{
              _web: {
                transform: `scale(${scaleValue}) translateX(${translateX}px) translateY(${translateY}px)`,
              },
            }}
          >
            <div ref={diagramRef} />
          </Box>
        </div>
      </Box>
    </Box>
  );
};

export default MermaidDiagram;
